import * as React from 'react';
import { ArrowDownRight, ArrowUpRight, Minus, type LucideIcon } from 'lucide-react';

import { cn } from '@repo/ui/lib/utils';
import { LimitProgressBar, type LimitProgressStatus } from './limit-progress-bar';
import { LiquidSurface } from './liquid-surface';
import type { StatusTone } from './status-badge';
import { Tooltip, TooltipContent, TooltipTrigger, useTapTooltip } from './tooltip';
import { HINT_CONTENT_CLASS_NAME, HintContent, type HintRow } from './info-label';

type MetricTone = StatusTone;
type MetricTrendDirection = 'up' | 'down' | 'flat';

interface MetricStatus {
  label: React.ReactNode;
  tone?: StatusTone;
  /** Render the status dot with a slow pulse — for continuously refreshed values. */
  live?: boolean;
  /**
   * Hover and focus explanation for the status word — e.g. why a value
   * cannot be calculated yet. Keep it one or two plain sentences.
   */
  hint?: string;
}

interface MetricTrend {
  label: React.ReactNode;
  direction?: MetricTrendDirection;
  tone?: MetricTone;
  showIcon?: boolean;
}

interface MetricProgress {
  pct: number;
  label?: React.ReactNode;
  status?: LimitProgressStatus;
}

interface MetricProps extends Omit<React.ComponentProps<'div'>, 'title'> {
  title: React.ReactNode;
  /**
   * Definition for the metric, shown on hovering or focusing the card.
   * Every KPI on a professional surface should be able to say what it is,
   * and how it was worked out, without printing that on the card.
   */
  hint?: React.ReactNode;
  /** Optional breakdown under the definition — the numbers behind it. */
  hintRows?: HintRow[];
  value: React.ReactNode;
  subtitle?: React.ReactNode;
  /** One plain-language basis line, e.g. "YTD · +4.6 pp above benchmark". */
  description?: React.ReactNode;
  /** One provenance line, e.g. "Cash-flow-adjusted TWR". */
  provenance?: React.ReactNode;
  icon?: LucideIcon;
  /** Use for asset marks or another non-Lucide visual. */
  leadingVisual?: React.ReactNode;
  status?: MetricStatus;
  trend?: MetricTrend;
  /** Only provide progress when the metric has a meaningful denominator. */
  progress?: MetricProgress;
  tone?: MetricTone;
  /**
   * Colors the value alone — the number's sentiment (a loss reads red)
   * without recoloring the card border or its status word.
   */
  valueTone?: MetricTone;
  valueClassName?: string;
  visualClassName?: string;
}

const toneClassName: Record<MetricTone, string> = {
  neutral: 'border-rowa-border-glass-highlight',
  positive: 'border-rowa-border-accent',
  warning: 'border-rowa-status-warning/35',
  error: 'border-rowa-status-error/35'
};

const valueToneClassName: Record<MetricTone, string> = {
  neutral: 'text-rowa-text-primary',
  positive: 'text-rowa-status-positive',
  warning: 'text-rowa-status-warning',
  error: 'text-rowa-status-error'
};

/**
 * Header status color (v1 node 888:1481): routine states read as quiet
 * tertiary text beside a brand-green dot; only warning/error states color
 * the word itself.
 */
const statusToneClassName: Record<MetricTone, string> = {
  neutral: 'text-rowa-text-tertiary',
  positive: 'text-rowa-text-tertiary',
  warning: 'text-rowa-status-warning',
  error: 'text-rowa-status-error'
};

const statusDotClassName: Record<MetricTone, string> = {
  neutral: 'bg-rowa-text-brand',
  positive: 'bg-rowa-text-brand',
  warning: 'bg-rowa-status-warning',
  error: 'bg-rowa-status-error'
};

const progressStatusByTone: Record<MetricTone, LimitProgressStatus> = {
  neutral: 'neutral',
  positive: 'within-limit',
  warning: 'near-limit',
  error: 'exceeded'
};

const trendIcon: Record<MetricTrendDirection, React.ReactNode> = {
  up: <ArrowUpRight aria-hidden className="size-3.5" />,
  down: <ArrowDownRight aria-hidden className="size-3.5" />,
  flat: <Minus aria-hidden className="size-3.5" />
};

/**
 * The dot-and-word state. A status carrying a `hint` becomes hoverable and
 * focusable, and explains itself in a tooltip — e.g. why a value cannot be
 * calculated yet. Exported on its own so a surface that is not a metric
 * card (a chart panel reading a live series) can carry the same dot and
 * word from the same tokens rather than redraw them.
 */
function MetricStatusWord({
  status,
  statusTone
}: {
  status: MetricStatus;
  statusTone: MetricTone;
}) {
  const word = (
    <span
      data-slot="metric-status"
      data-tone={statusTone}
      tabIndex={status.hint ? 0 : undefined}
      className={cn(
        'text-rowa-label-sm flex items-center gap-1.5',
        statusToneClassName[statusTone],
        status.hint &&
          'focus-visible:ring-rowa-border-accent cursor-help rounded-sm focus-visible:ring-2 focus-visible:outline-none'
      )}
    >
      <span className="relative flex size-[7px]" aria-hidden>
        {status.live && (
          <span
            className={cn(
              'absolute inline-flex h-full w-full rounded-full opacity-60 motion-safe:animate-ping motion-safe:[animation-duration:2.4s]',
              statusDotClassName[statusTone]
            )}
          />
        )}
        <span
          className={cn(
            'relative inline-flex size-[7px] rounded-full',
            statusDotClassName[statusTone]
          )}
        />
      </span>
      {status.label}
    </span>
  );

  if (!status.hint) return word;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{word}</TooltipTrigger>
      <TooltipContent className="max-w-64">{status.hint}</TooltipContent>
    </Tooltip>
  );
}

/**
 * Institutional KPI card (Figma "ROWA / Metric / Institutional / Liquid",
 * node 890:1475): one clear metric, its period or basis, and one provenance
 * line. The header label is a brand-green eyebrow; state is a dot + written
 * word so it never relies on color alone. Width stays grid-fluid while the
 * minimum height preserves the approved vertical rhythm.
 */
function Metric({
  title,
  hint,
  hintRows,
  value,
  subtitle,
  description,
  provenance,
  icon: Icon,
  leadingVisual,
  status,
  trend,
  progress,
  tone = 'neutral',
  valueTone,
  valueClassName,
  visualClassName,
  className,
  ...props
}: MetricProps) {
  const titleId = React.useId();
  const tap = useTapTooltip();
  const trendTone = trend?.tone ?? tone;
  const statusTone = status?.tone ?? tone;
  const explained = Boolean(hint || hintRows?.length);
  // One hover per card: a status that explains itself inside an explained
  // card says so in the card's panel, never in a second tooltip on top.
  const statusHint = explained ? status?.hint : undefined;
  const shownStatus = statusHint && status ? { ...status, hint: undefined } : status;

  const card = (
    <LiquidSurface
      type="panel"
      role="group"
      aria-labelledby={titleId}
      data-slot="metric"
      data-tone={tone}
      // Reachable by keyboard when it has something to say, so the
      // explanation is never hover-only.
      tabIndex={explained ? 0 : undefined}
      className={cn(
        'flex w-full min-w-0 flex-col gap-[7px] border p-[18px]',
        toneClassName[tone],
        explained &&
          'focus-visible:ring-rowa-border-accent focus-visible:ring-2 focus-visible:outline-none',
        className
      )}
      {...props}
    >
      <div className="flex min-w-0 items-start justify-between gap-3">
        <div className="min-w-0">
          <div
            id={titleId}
            className="text-rowa-label-sm text-rowa-text-brand tracking-[0.08em] uppercase"
          >
            {explained ? (
              // The dotted underline marks the card as one that explains
              // itself; the card, not the word, is what opens it.
              <span
                role="note"
                className="decoration-rowa-text-tertiary/60 underline decoration-dotted underline-offset-4"
              >
                {title}
              </span>
            ) : (
              title
            )}
          </div>
          {subtitle && (
            <div className="text-rowa-label-sm text-rowa-text-tertiary mt-0.5">{subtitle}</div>
          )}
        </div>

        {(shownStatus || leadingVisual || Icon) && (
          <div className="flex shrink-0 items-center gap-2">
            {shownStatus && <MetricStatusWord status={shownStatus} statusTone={statusTone} />}
            {(leadingVisual || Icon) && (
              <span
                data-slot="metric-visual"
                className={cn(
                  'border-rowa-border-subtle bg-rowa-surface-inset text-rowa-text-primary rounded-rowa-md flex size-9 shrink-0 items-center justify-center overflow-hidden border',
                  visualClassName
                )}
              >
                {leadingVisual ?? (Icon ? <Icon aria-hidden className="size-4" /> : null)}
              </span>
            )}
          </div>
        )}
      </div>

      <div
        data-slot="metric-value"
        className={cn(
          'text-rowa-data-hero tracking-[-0.5px] tabular-nums',
          valueToneClassName[valueTone ?? tone],
          valueClassName
        )}
      >
        {value}
      </div>

      {trend && (
        <div
          data-slot="metric-trend"
          data-tone={trendTone}
          className={cn(
            'text-rowa-label-md flex items-center gap-1',
            valueToneClassName[trendTone]
          )}
        >
          {trend.showIcon !== false && trendIcon[trend.direction ?? 'flat']}
          {trend.label}
        </div>
      )}

      {description && (
        <div className="text-rowa-body-md text-rowa-text-secondary">{description}</div>
      )}

      {provenance && (
        <div
          data-slot="metric-provenance"
          className="text-rowa-label-sm text-rowa-text-tertiary tracking-[0.02em]"
        >
          {provenance}
        </div>
      )}

      {progress && (
        <LimitProgressBar
          className="pt-1"
          pct={progress.pct}
          label={progress.label}
          status={progress.status ?? progressStatusByTone[tone]}
        />
      )}
    </LiquidSurface>
  );

  if (!explained) return card;

  return (
    // A short delay: sweeping the pointer across a row of cards should not
    // flash every panel on the way.
    <Tooltip delayDuration={200} {...tap.rootProps}>
      <TooltipTrigger asChild {...tap.triggerProps}>
        {card}
      </TooltipTrigger>
      <TooltipContent
        side="bottom"
        align="start"
        sideOffset={8}
        className={HINT_CONTENT_CLASS_NAME}
      >
        <HintContent
          title={title}
          hint={
            statusHint ? (
              <>
                {hint ? <p>{hint}</p> : null}
                <p className={hint ? 'mt-1.5' : undefined}>{statusHint}</p>
              </>
            ) : (
              hint
            )
          }
          rows={hintRows}
        />
      </TooltipContent>
    </Tooltip>
  );
}

export { Metric, MetricStatusWord };
export type {
  MetricProgress,
  MetricProps,
  MetricStatus,
  MetricTone,
  MetricTrend,
  MetricTrendDirection
};
